import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Menu, X, Phone, Mail, Star, Instagram, Linkedin } from "lucide-react";
import { useState } from "react";
import poppinsLogo from "@/assets/logo-nextconvert.png";
import logoIcon from "@/assets/logo-nextconvert.png";
import logoWit from "@/assets/logo-nextconvert.png";

import { useCookieConsent } from "@/contexts/CookieConsentContext";

// Flag components - Round flags
const FlagNL = () => <svg className="w-6 h-6 rounded-full overflow-hidden" viewBox="0 0 512 512">
    <clipPath id="nl-clip"><circle cx="256" cy="256" r="256" /></clipPath>
    <g clipPath="url(#nl-clip)">
      <rect width="512" height="170.7" fill="#AE1C28" />
      <rect y="170.7" width="512" height="170.7" fill="#FFF" />
      <rect y="341.4" width="512" height="170.6" fill="#21468B" />
    </g>
  </svg>;
const FlagEN = () => <svg className="w-6 h-6 rounded-full overflow-hidden" viewBox="0 0 512 512">
    <clipPath id="en-clip"><circle cx="256" cy="256" r="256" /></clipPath>
    <g clipPath="url(#en-clip)">
      <rect width="512" height="512" fill="#012169" />
      <path d="M0,0 L512,512 M512,0 L0,512" stroke="#FFF" strokeWidth="60" />
      <path d="M0,0 L512,512 M512,0 L0,512" stroke="#C8102E" strokeWidth="40" />
      <path d="M256,0 V512 M0,256 H512" stroke="#FFF" strokeWidth="100" />
      <path d="M256,0 V512 M0,256 H512" stroke="#C8102E" strokeWidth="60" />
    </g>
  </svg>;
const FlagDE = () => <svg className="w-6 h-6 rounded-full overflow-hidden" viewBox="0 0 512 512">
    <clipPath id="de-clip"><circle cx="256" cy="256" r="256" /></clipPath>
    <g clipPath="url(#de-clip)">
      <rect width="512" height="170.7" fill="#000" />
      <rect y="170.7" width="512" height="170.7" fill="#D00" />
      <rect y="341.4" width="512" height="170.6" fill="#FFCE00" />
    </g>
  </svg>;
const Layout = ({
  children
}: {children: React.ReactNode;}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const location = useLocation();
  const { reopen: openCookieSettings } = useCookieConsent();
  const phoneNumber = "0620192358";
  const whatsappMessage = encodeURIComponent("Hallo, ik heb interesse in je diensten!");
  const handleCloseMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setMobileMenuOpen(false);
      setIsClosing(false);
    }, 250);
  };
  const isActive = (href: string) => location.pathname === href;
  const openExternalLink = (url: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return <div className="min-h-screen bg-background overflow-x-hidden">

    <div className="sticky top-0 z-50 w-full bg-primary">
    {/* Safe area boven de notch blijft donkerblauw */}
    <div className="w-full bg-primary" style={{ height: "env(safe-area-inset-top)" }} />
    {/* Aankondigingsbalk */}
    <div className="w-full bg-[#f96706] text-white text-center px-4 py-2 text-[13px] sm:text-sm font-medium">
      Nog maar plek voor 2 nieuwe klanten voor Q4
    </div>


    {/* Main Header - Webtify Style */}
    <header className="w-full bg-primary shadow-[0_1px_0_rgba(255,255,255,0.15)]">

      <nav className="container mx-auto px-2 lg:px-4 max-w-[1400px] my-0 py-[22px]" aria-label="Hoofdnavigatie">
        {/* Mobile version */}
        <div className="md:hidden flex h-10 items-center justify-between">
          <Link to="/" className="flex items-center">
            <img alt="Nextconvert logo" width={100} height={25} className="h-6 w-auto object-contain" loading="eager" src={logoWit} />
          </Link>

          <button type="button" className="flex items-center justify-center w-10 h-10 text-primary-foreground" onClick={() => mobileMenuOpen ? handleCloseMenu() : setMobileMenuOpen(true)} aria-label={mobileMenuOpen ? "Sluit navigatiemenu" : "Open navigatiemenu"} aria-expanded={mobileMenuOpen} aria-controls="mobile-nav">
            {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Desktop version */}
        <div className="hidden md:flex h-16 items-center justify-between gap-4">
          {/* Logo - Left */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img alt="Nextconvert logo" width={130} height={33} className="h-7 lg:h-8 w-auto object-contain" loading="eager" src={logoWit} />
            </Link>
          </div>

          {/* Right: Navigation + CTA */}
          <div className="flex items-center gap-1">
            <Link to="/shopify-webshop" className="inline-flex items-center px-3 lg:px-4 h-10 text-base font-medium text-white hover:text-white/80 transition-colors">
              Shopify webshop
            </Link>




            <Link to="/email-marketing" className="inline-flex items-center px-3 lg:px-4 h-10 text-base font-medium text-white hover:text-white/80 transition-colors">
              E-mailmarketing
            </Link>

            <Link to="/portfolio" className="inline-flex items-center px-3 lg:px-4 h-10 text-base font-medium text-white hover:text-white/80 transition-colors">
              Cases
            </Link>


            <Link to="/over-ons" className="inline-flex items-center px-3 lg:px-4 h-10 text-base font-medium text-white hover:text-white/80 transition-colors">
              Over ons
            </Link>

            <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 h-10 rounded-full border-2 border-accent ml-3">
              <Link to="/kennismaken">Kennismaken</Link>
            </Button>
          </div>
        </div>
      </nav>
    </header>
    </div>

    {/* Grey separator line between header and content */}
    <div className="hidden md:block h-[0.3px] bg-gray-500/15" />

    {/* Mobile Navigation - Full screen panel below header */}
    {mobileMenuOpen && <div className="md:hidden fixed inset-x-0 top-[120px] bottom-0 z-40">
        {/* Panel - full screen dark blue */}
        <nav id="mobile-nav" className={`absolute inset-0 bg-primary text-primary-foreground overflow-y-auto flex flex-col ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} aria-label="Mobiele navigatie">
          {/* Navigation Links */}
          <div className="px-6 pt-4 flex-1 divide-y divide-primary-foreground/10">

            {/* Shopify webshop */}
            <Link to="/shopify-webshop" className="block py-4 text-primary-foreground text-lg font-medium" onClick={handleCloseMenu}>
              Shopify webshop
            </Link>

            {/* E-mailmarketing */}
            <Link to="/email-marketing" className="block py-4 text-primary-foreground text-lg font-medium" onClick={handleCloseMenu}>
              E-mailmarketing
            </Link>

            {/* AI Oplossingen */}
            <Link to="/ai-oplossingen" className="flex items-center py-4 text-primary-foreground text-lg font-medium" onClick={handleCloseMenu}>
              AI E-commerce
              <span className="ml-2 text-sm">✨</span>
            </Link>

            {/* Over ons */}
            <Link to="/over-ons" className="block py-4 text-primary-foreground text-lg font-medium" onClick={handleCloseMenu}>
              Over ons
            </Link>
          </div>

          {/* Bottom CTA */}
          <div className="px-6 pt-6 pb-10">
            <Button asChild className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-12 rounded-full text-base">
              <Link to="/kennismaken" onClick={handleCloseMenu}>
                Plan gratis gesprek
              </Link>
            </Button>
          </div>

        </nav>
      </div>}

    {/* Main Content */}
    <main>{children}</main>

    {/* Footer */}
    <footer className="bg-[#01152f]">
      <div className="container mx-auto px-6 py-16 lg:py-20 max-w-[1400px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
          {/* Column 1: Logo + Info */}
          <div>
            <img src={logoWit} alt="Nextconvert logo" width={120} height={30} className="h-7 w-auto object-contain mb-5" loading="lazy" />
            <p className="text-primary-foreground/70 mb-6 leading-relaxed">
              Shopify webshops, websites en AI voor e-commerce die jouw business laten groeien.
            </p>
            <ul className="space-y-1 text-primary-foreground/60 text-sm">
              <li>Valburg, Gelderland</li>
              <li>Tielsestraat 21, 6675AA</li>
              <li className="pt-2">
                <a href="tel:0620192358" className="hover:text-primary-foreground transition-colors">06 20192358</a>
              </li>
              <li>
                <a href="mailto:info@nextconvert.nl" className="hover:text-primary-foreground transition-colors">info@nextconvert.nl</a>
              </li>
            </ul>
          </div>

          {/* Column 2: Diensten */}
          <div>
            <h3 className="text-lg font-bold text-primary-foreground mb-5"><span className="accent-serif">Diensten</span></h3>
            <ul className="space-y-3">
              <li><Link to="/shopify-webshop" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">Shopify webshop</Link></li>
              <li><Link to="/tiktok-shop" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">TikTok Shop</Link></li>
              <li><Link to="/websitedesign" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">Website laten maken</Link></li>
              <li><Link to="/email-marketing" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">E-mailmarketing</Link></li>
              <li><Link to="/ai-oplossingen" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">AI E-commerce</Link></li>
            </ul>
          </div>

          {/* Column 3: Ontdekken */}
          <div>
            <h3 className="text-lg font-bold text-primary-foreground mb-5"><span className="accent-serif">Ontdekken</span></h3>
            <ul className="space-y-3">
              <li><Link to="/portfolio" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">Cases</Link></li>
              <li><Link to="/blogs" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">Blog</Link></li>
              <li><Link to="/over-ons" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">Over ons</Link></li>
              <li><Link to="/offerte" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors">Offerte aanvragen</Link></li>
              <li>
                <Link to="/vacatures" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Vacatures
                  <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold rounded-full bg-accent text-accent-foreground">2</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Volg ons */}
          <div>
            <h3 className="text-lg font-bold text-primary-foreground mb-5">Volg <span className="accent-serif">ons</span></h3>
            <div className="flex gap-3">
              <a href="https://www.instagram.com/nextconvert/" target="_blank" rel="noopener noreferrer external" onClick={openExternalLink("https://www.instagram.com/nextconvert/")} className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors" aria-label="Volg Nextconvert op Instagram">
                <Instagram className="h-5 w-5 text-primary-foreground" aria-hidden="true" />
              </a>
              <a href="https://www.tiktok.com/@nielssjansen" target="_blank" rel="noopener noreferrer external" onClick={openExternalLink("https://www.tiktok.com/@nielssjansen")} className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors" aria-label="Volg Nextconvert op TikTok">
                <svg className="h-5 w-5 text-primary-foreground" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52v-3.4a4.85 4.85 0 01-1-.16z" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/in/niels-jansen-b15495396/" target="_blank" rel="noopener noreferrer external" onClick={openExternalLink("https://www.linkedin.com/in/niels-jansen-b15495396/")} className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors" aria-label="Volg Nextconvert op LinkedIn">
                <Linkedin className="h-5 w-5 text-primary-foreground" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-6 py-4 max-w-[1400px] flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="text-sm text-primary-foreground/40">&copy; {new Date().getFullYear()} Nextconvert. Alle rechten voorbehouden.</span>
          <Link to="/privacybeleid" className="text-sm text-primary-foreground/40 hover:text-primary-foreground transition-colors">Privacy Policy</Link>
          <button type="button" onClick={openCookieSettings} className="text-sm text-primary-foreground/40 hover:text-primary-foreground transition-colors">Cookievoorkeuren</button>
        </div>
      </div>
    </footer>

    {/* Floating Chat Widget */}
  </div>;
};
export default Layout;